import { getUtmParams, UTM_PARAM_KEYS, CLICK_ID_KEYS } from '../browser'
import { ACQUISITION_STORAGE_KEY } from '../constants'

/**
 * First-touch acquisition context for the session: `referrer` + `utm_*` +
 * `gclid`, captured the FIRST time in a session and then frozen until the
 * session rolls over.
 *
 * Why first-touch: these describe where/why the session started — an immutable
 * fact. Re-reading them on every load/navigation (the original behaviour) let a
 * later internal page overwrite the real entry source, degrading attribution.
 *
 * Session boundary: aligned with Faro's session lifetime — reset after
 * {@link INACTIVITY_MS} of inactivity OR {@link MAX_LIFETIME_MS} total, matching
 * Faro's `SESSION_INACTIVITY_TIME` (15min) / `SESSION_EXPIRATION_TIME` (4h)
 * defaults. Persisted to localStorage (cross-tab, survives reload) — like
 * Faro's persistent session — NOT sessionStorage, which is per-tab and would
 * neither survive the session's own rollover nor match Faro's storage.
 *
 * Only the acquisition ORIGIN is frozen here; session STATE (currency, location,
 * user, is_pwa) stays mutable elsewhere.
 */

/** Faro `SESSION_INACTIVITY_TIME` default. */
const INACTIVITY_MS = 15 * 60 * 1000
/** Faro `SESSION_EXPIRATION_TIME` default. */
const MAX_LIFETIME_MS = 4 * 60 * 60 * 1000

interface StoredAcquisition {
  acquisition: Record<string, string>
  firstSeen: number
  lastSeen: number
}

function read(): StoredAcquisition | null {
  try {
    const raw = window.localStorage.getItem(ACQUISITION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredAcquisition
    return parsed && parsed.acquisition && typeof parsed.firstSeen === 'number' ? parsed : null
  } catch {
    return null
  }
}

function write(value: StoredAcquisition): void {
  try {
    window.localStorage.setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // storage blocked (private mode / iframe) — capture still works for this load
  }
}

/** Whether a stored acquisition is still within the live session window. */
function isLive(stored: StoredAcquisition, nowMs: number): boolean {
  return nowMs - stored.lastSeen < INACTIVITY_MS && nowMs - stored.firstSeen < MAX_LIFETIME_MS
}

/**
 * Whether the current URL carries a utm/gclid that DIFFERS from the frozen
 * acquisition. Returns false when the URL has no utm at all (internal
 * navigation must never reset the origin).
 */
function hasNewUtm(searchParams: URLSearchParams, storedAcquisition: Record<string, string>): boolean {
  const current = getUtmParams(searchParams) as Record<string, string>
  const keys = Object.keys(current)
  if (keys.length === 0) return false
  return keys.some(key => current[key] !== storedAcquisition[key])
}

/** Capture a fresh first touch (utm/gclid + referrer) and persist it. */
function capture(searchParams: URLSearchParams, nowMs: number): Record<string, string> {
  const acquisition: Record<string, string> = { ...getUtmParams(searchParams) }
  const referrer = typeof document !== 'undefined' ? document.referrer : ''
  if (referrer) acquisition.referrer = referrer
  write({ acquisition, firstSeen: nowMs, lastSeen: nowMs })
  return acquisition
}

// Set when getAcquisition rolls the session over due to a new utm (see
// `resetOnNewUtm`). Consumed by the caller to start a fresh Faro session.
let rolledOverByUtm = false

/** Read-and-clear the "rolled over due to a new utm" flag. */
export function consumeAcquisitionRollover(): boolean {
  const v = rolledOverByUtm
  rolledOverByUtm = false
  return v
}

/**
 * First-touch acquisition for the current session. Captures fresh when there's
 * no live session; otherwise returns the frozen origin and extends the session
 * (updates `lastSeen`).
 *
 * When `opts.resetOnNewUtm` is set and the URL brings a utm different from the
 * frozen one, the session ROLLS OVER (fresh capture) — GA4-style — and the
 * rollover flag is raised for the caller to start a new Faro session.
 */
export function getAcquisition(
  searchParams: URLSearchParams,
  opts: { resetOnNewUtm?: boolean } = {},
): Record<string, string> {
  if (typeof window === 'undefined') return {}

  const nowMs = Date.now()
  const stored = read()
  if (stored && isLive(stored, nowMs)) {
    if (opts.resetOnNewUtm && hasNewUtm(searchParams, stored.acquisition)) {
      rolledOverByUtm = true
      return capture(searchParams, nowMs) // new campaign → new first touch
    }
    write({ ...stored, lastSeen: nowMs }) // keep the session alive
    return stored.acquisition
  }

  // No live session (first visit / rolled over) → capture a new first touch.
  return capture(searchParams, nowMs)
}

/** UTM/click-id keys the acquisition tracks — used to strip stale utm on reset. */
export const ACQUISITION_UTM_KEYS: readonly string[] = [...UTM_PARAM_KEYS, ...CLICK_ID_KEYS]

/**
 * Extend the acquisition session (bump `lastSeen`) without changing the frozen
 * origin — called on navigation so an active SPA visit doesn't expire the
 * session and lose its first touch. No-op when nothing is stored or it already
 * rolled over (a fresh capture happens on the next getAcquisition).
 */
export function touchAcquisition(): void {
  if (typeof window === 'undefined') return
  const stored = read()
  if (!stored) return
  const nowMs = Date.now()
  if (isLive(stored, nowMs)) write({ ...stored, lastSeen: nowMs })
}
